import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import {
  AlertComponent,
  SelectInputComponent,
} from "../../../../common/components";
import {
  cityOptions,
  stateOptions,
  codeOptions,
} from "../../../../utils/extractStateCityZipCode";
import { RightPanelBodyProps } from "../lead-assignment-props";
import ListOfPartnersBasedOnRule from "./list-partners-based-on-rule";
import {
  fetchPartnersByRule,
  createRule,
} from "../../../../api/lead-assignment";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../constants/routes";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { Partner } from "./partner";
import SingleSelectAutocomplete from "./single-option-select";
import MultiSelectAutocomplete from "./multi-option-auto-select";

const RightPanelBody: React.FC<RightPanelBodyProps> = ({
  activeStep,
  setActiveStep,
}) => {
  const { values, setFieldValue, handleSubmit, errors, resetForm } =
    useFormikContext<any>();
  const countryOptions = [{ label: "United States", value: "United States" }];
  const [partners, setPartners] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [addedPartners, setAddedPartners] = useState<Partner[]>([]);
  const [matchedPartners, setMatchedPartners] = useState<Partner[]>([]);
  const [fetchedPartners, setFetchedPartners] = useState<Partner[]>([]); // New state to store fetched partners
  const handleAdd = (partner: Partner) => {
    setMatchedPartners((prevMatched) =>
      prevMatched.filter((p) => p.id !== partner.id)
    );

    setAddedPartners((prevAdded) => {
      const alreadyAdded = prevAdded.some((p) => p.id === partner.id);
      return alreadyAdded ? prevAdded : [...prevAdded, partner];
    });
  };

  const handleRemove = (partner: Partner) => {
    setAddedPartners((prevAdded) =>
      prevAdded.filter((p) => p.id !== partner.id)
    );

    setMatchedPartners((prevMatched) => {
      const alreadyMatched = prevMatched.some((p) => p.id === partner.id);
      return alreadyMatched ? prevMatched : [...prevMatched, partner];
    });
  };

  const geometricCriteriaOption = [
    { id: 1, label: "Country", value: "Country" },
    { id: 2, label: "State", value: "State" },
    { id: 3, label: "City", value: "City" },
    { id: 4, label: "ZipCode", value: "ZipCode" },
  ];

  const andOrOptions = [{ id: 1, label: "And", value: "And" }];

  const productCriteriaOptions = [
    { id: 1, label: "At-Need", value: "At-Need" },
    { id: 2, label: "Preneed", value: "Preneed" },
  ];

  const handleReset = () => {
    setAddedPartners([]);
    setMatchedPartners([]);
    resetForm();
  };

  const geoFieldMap: Record<string, string> = {
    Country: "country",
    State: "state",
    City: "city",
    ZipCode: "zipcode",
  };

  const handleProceed = async () => {
    let geoKey = geoFieldMap[values.geographyParam] || "";
    let geographyValue = values[geoKey];
    const productType = values.productType;

    if (values.geographyParam === "ZipCode") {
      geoKey = "postcode";
      geographyValue = values.zipcode;
    }

    const params = {
      isActive: true,
      geographyParam: geoKey,
      geographyValue: geographyValue || "",
      productType:productType || [],
    };
    if (Object.keys(errors).length === 0) {
      setLoading(true);
      try {
        setAddedPartners([]);
        setMatchedPartners([]);
        const response = await fetchPartnersByRule(params);
        const fetchedPartners = response.data.data || [];
        setAddedPartners(fetchedPartners);
        setFetchedPartners(fetchedPartners);

        if (activeStep < 2) {
          setActiveStep(activeStep + 1);
        }
      } catch (error) {
        console.error("Failed to fetch partners", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveRule = async () => {
    const geoKey = geoFieldMap[values.geographyParam] || "";
    const geographyValue = values[geoKey];

    const payload = {
      ruleName: values.ruleName,
      isActive: values.isActive === "Active" ? true : false,
      partnerIds: addedPartners.map((partner: any) => partner.id),
      distributionMethod: "RoundRobin",
      ruleCriteria: {
        geographyParam: geoKey || null,
        geographyValue: geographyValue || null,
        productType: values.productType || [],
        conditionType: "AND", // Assuming conditionType is always "And" based on the current implementation
      },
    };

    if (Object.keys(errors).length === 0) {
      setLoading(true);
      try {
        const response = await createRule(payload);
        if (response.data.success) {
          setLoading(false);
          navigate(routes.ASSIGNMENTRULES, {
            state: {
              message: "Assignment rule created successfully.",
              type: "success",
            },
          });
        } else {
          setAlertMessage("Failed to create rule.");
          setAlertType("error");
          setAlertOpen(true);
          setLoading(false);
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "An error occurred while creating the rule."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemovePartner = (emailToRemove: string) => {
    // logic to remove partner from state
  };
  const handleClearMatchedPartners = () => {
    setAddedPartners(fetchedPartners);
    setMatchedPartners([]);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  const geoKey = geoFieldMap[values.geographyParam] || "";
  const geoValue = values[geoKey] || [];
  const isProceedEnabled = values.geographyParam && geoValue.length > 0 && values.productType && values.productType.length > 0;

  return (
    <>
      <div className="right-panel-body">
        {activeStep === 0 && (
          <>
            <div className="criteria-wrapper" style={{ position: "relative" }}>
              <div className="vertical-dotted-line" />
              <div className="horizontal-geography-dotted-line" />
              <div className="horizontal-product-dotted-line" />

              {/* Geography Criteria */}
              <div className="geometric-criteria-wrapper">
                <h4 className="text-header">Geography Criteria</h4>
                <div className="geometric-criteria-dropdowns-wrapper">
                  <div className="geometric-criteria-dropdown">
                    <SelectInputComponent
                      fieldName="geographyParam"
                      options={geometricCriteriaOption}
                      value={values.geographyParam}
                      placeholder="Select Geography"
                      onChange={(val: string) =>
                        setFieldValue("geographyParam", val)
                      }
                    />
                  </div>

                  {values.geographyParam === "Country" && (
                    <div className="country-dropdown address-dropdown">
                      <SingleSelectAutocomplete
                        options={countryOptions}
                        selectedValue={
                          countryOptions.find(
                            (opt) => opt.value === values.country
                          ) || null
                        }
                        placeholder="Please Select Country"
                        onChange={(val: any) =>
                          setFieldValue("country", val?.value || "")
                        }
                        noOptionsFoundText={"No matching countries found"}
                      />
                    </div>
                  )}

                  {values.geographyParam === "State" && (
                    <div className="state-dropdown address-dropdown">
                      <SingleSelectAutocomplete
                        options={stateOptions}
                        selectedValue={
                          stateOptions.find(
                            (opt) => opt.value === values.state
                          ) || null
                        }
                        placeholder="Please Select State"
                        onChange={(val: any) =>
                          setFieldValue("state", val?.value || "")
                        }
                        noOptionsFoundText={"No matching states found"}
                      />
                    </div>
                  )}

                  {values.geographyParam === "City" && (
                    <div className="city-dropdown address-dropdown">
                      <SingleSelectAutocomplete
                        options={cityOptions}
                        selectedValue={
                          cityOptions.find(
                            (opt) => opt.value === values.city
                          ) || null
                        }
                        placeholder="Please Select City"
                        onChange={(val: any) =>
                          setFieldValue("city", val?.value || "")
                        }
                        noOptionsFoundText={"No matching cities found"}
                      />
                    </div>
                  )}

                  {values.geographyParam === "ZipCode" && (
                    <div className="zipCode-dropdown address-dropdown">
                      <SingleSelectAutocomplete
                        options={codeOptions}
                        selectedValue={
                          codeOptions.find(
                            (opt) => opt.value === values.zipcode
                          ) || null
                        }
                        placeholder="Please Select Zipcode"
                        onChange={(val: any) =>
                          setFieldValue("zipcode", val?.value || "")
                        }
                        noOptionsFoundText={"No matching zipcodes found"}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Condition */}
              <div className="condition-wrapper">
                <span>Condition</span>
                <div className="and-or-dropdown">
                  {/* <SelectInputComponent
                    fieldName="conditionType"
                    options={andOrOptions}
                    value={values.conditionType}
                    placeholder="And"
                    onChange={(val: string) =>
                      setFieldValue("conditionType", val)
                    }
                  /> */}
                  <span className="condition-label">And</span>
                </div>
              </div>

              {/* Product Criteria */}
              <div className="product-criteria-wrapper">
                <h4 className="text-header product-header">Product Criteria</h4>
                <div className="product-criteria-dropdowns-wrapper">
                  <div className="product-criteria-dropdown">
                    {/* for single selec */}
                    {/* <SelectInputComponent
                      fieldName="productType"
                      options={productCriteriaOptions}
                      value={values.productType}
                      placeholder="Select Product"
                      onChange={(val: string) =>
                        setFieldValue("productType", val)
                      }
                    /> */}
                    {/* for multi select */}
                    <MultiSelectAutocomplete
                      fieldName="productType"
                      options={productCriteriaOptions}
                      selectedValues={values.productType || []}
                      placeholder="Select Product"
                      onChange={(val) =>
                        setFieldValue("productType", val)
                      }
                      noResultsTxt="No matching products found"
                    />
                    {/* {errors.productType && (
                      <div className="error-message">{errors.productType}</div>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
            <div className="button-wrapper">
              <div className="custom-btn-wrapper">
                <button
                  type="button"
                  className="custom-btn reset-btn"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
              <div className="custom-btn-wrapper">
                <button
                  type="submit"
                  className={`custom-btn proceed-btn ${!isProceedEnabled ? "disabled" : ""}`}
                  onClick={handleProceed}
                  disabled={!isProceedEnabled}
                >
                  Proceed
                </button>
              </div>
            </div>
          </>
        )}

        {activeStep === 1 && (
          <div className="assign-rule-wrapper">
            <ListOfPartnersBasedOnRule
              distributionType="Round Robin"
              addedPartners={addedPartners}
              matchedPartners={matchedPartners}
              onAdd={handleAdd}
              onRemove={handleRemove}
            />
            <div className="button-wrapper">
              <div className="custom-btn-wrapper">
                <button
                  type="button"
                  className="custom-btn reset-btn"
                  onClick={handleClearMatchedPartners}
                >
                  Reset
                </button>
              </div>
              <div className="custom-btn-wrapper">
                <button
                  type="button"
                  className="custom-btn proceed-btn"
                  onClick={handleSaveRule}
                  disabled={!(addedPartners.length > 0)}
                >
                  Save Rule
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default RightPanelBody;
